import { useEffect, useRef, useState, useLayoutEffect } from "react";
import styles from "../ScrollStyles.module.scss";
import accreditationStyles from "./AccreditationPill.module.scss";
import Portal from "@Components/Portal";
import PropertyDetailsModal from "../../../PropertyDetailsModal";
import Image from "@Components/Image";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import AccreditationBadge from "@Icons/AccreditationBadge";
import useTranslationHook from "hooks/useTranslationHook";

interface Accreditation {
  id: number;
  name: string;
  title: string;
  description?: string;
  logo_url?: string;
  accreditation_type?: string;
}

interface AccreditationPillProps {
  accreditations: Accreditation[];
  isDesktop?: boolean;
}

const AccreditationList = ({ accreditations }: { accreditations: Accreditation[] }) => (
  <div className={accreditationStyles.tooltipContent}>
    {accreditations?.map((accreditation) => (
      <div
        key={accreditation.id}
        className={accreditationStyles.accreditationItem}
        data-testid={`accreditation-item-${accreditation.id}`}
      >
        <div className={accreditationStyles.logoContainer}>
          {accreditation.logo_url ? (
            <Image
              src={accreditation.logo_url}
              alt={accreditation.name}
              width={36}
              height={36}
              className={accreditationStyles.logo}
              isEagerLoad
            />
          ) : (
            <AccreditationBadge />
          )}
        </div>
        <div className={accreditationStyles.textContainer}>
          <span className={accreditationStyles.title} title={accreditation.title}>
            {accreditation.title}
          </span>
        </div>
      </div>
    ))}
  </div>
);

const AccreditationPill = ({ accreditations, isDesktop = false }: AccreditationPillProps) => {
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showDesktopPopover, setShowDesktopPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [arrowOffset, setArrowOffset] = useState(0);
  const pillRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<any>(null);
  const { t } = useTranslationHook();

  // Close desktop popover on scroll
  useEffect(() => {
    if (!showDesktopPopover) return undefined;
    const handleScroll = () => setShowDesktopPopover(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [showDesktopPopover]);

  // Recalculate position after popover renders to get its actual width
  useLayoutEffect(() => {
    if (!showDesktopPopover || !popoverRef.current || !pillRef.current) return;
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const pillRect = pillRef.current.getBoundingClientRect();
    const padding = 16;
    const pillCenterX = pillRect.left + pillRect.width / 2;
    const popoverWidth = popoverRect.width;

    let left = pillCenterX - popoverWidth / 2;
    let arrow = popoverWidth / 2;

    if (left < padding) {
      arrow = pillCenterX - padding;
      left = padding;
    } else if (left + popoverWidth > window.innerWidth - padding) {
      left = window.innerWidth - padding - popoverWidth;
      arrow = pillCenterX - left;
    }

    setPopoverPosition({ top: pillRect.top - 24, left });
    setArrowOffset(arrow);
  }, [showDesktopPopover, accreditations.length]);

  if (!accreditations?.length) return null;

  const handleMouseEnter = () => {
    if (!isDesktop) return;
    clearTimeout(hoverTimeoutRef.current);
    setShowDesktopPopover(true);
  };

  const handleMouseLeave = () => {
    if (!isDesktop) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setShowDesktopPopover(false);
    }, 200);
  };

  const handlePopoverMouseEnter = () => {
    clearTimeout(hoverTimeoutRef.current);
  };

  const handlePopoverMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setShowDesktopPopover(false);
    }, 200);
  };

  const handlePillClick = (e: any) => {
    e?.stopPropagation();
    if (!isDesktop) {
      setShowMobileModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowMobileModal(false);
  };

  const pillText = `${accreditations.length} ${
    accreditations.length === 1
      ? t("propertypage.accreditation.certificate")
      : t("propertypage.accreditation.certificates")
  }`;

  return (
    <>
      {/* Hidden prerender to warm up image cache */}
      <div
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0 }}
        aria-hidden="true"
      >
        <AccreditationList accreditations={accreditations} />
      </div>

      <div
        ref={pillRef}
        className={styles.parentContainer}
        onClick={handlePillClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") handlePillClick(e);
        }}
      >
        <div className={styles.journeyContainer} data-testid="accreditation-pill">
          <div className={styles.iconContainer}>
            <AccreditationBadge />
          </div>
          <div className={styles.info}>
            <div className={formatClassNames(styles.cityName, accreditationStyles.pillText)}>
              {pillText}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: Portal popover on hover */}
      {isDesktop && showDesktopPopover && (
        <Portal rootId="forPortal">
          <div
            ref={popoverRef}
            className={accreditationStyles.popoverContainer}
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
              transform: "translateY(-100%)",
            }}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
            role="none"
          >
            <AccreditationList accreditations={accreditations} />
            <div className={accreditationStyles.popoverArrow} style={{ left: arrowOffset }} />
          </div>
        </Portal>
      )}

      {/* Mobile: bottom sheet modal on click */}
      {!isDesktop && showMobileModal && (
        <Portal rootId="forPortal">
          <PropertyDetailsModal
            header="Certificates"
            hidePropertyModal={handleCloseModal}
            actionButtonText="Got it"
            actionButtonVariant="primary"
            actionButtonClick={handleCloseModal}
            bodyWithNoPadding
          >
            <div className={accreditationStyles.modalContent}>
              <AccreditationList accreditations={accreditations} />
            </div>
          </PropertyDetailsModal>
        </Portal>
      )}
    </>
  );
};

export default wrapperHOC(AccreditationPill, {
  componentName: "AccreditationPill-PropertyDetailsSection-PropertyPage",
  showForChina: true,
});
